import createNumberMask from 'text-mask-addons/dist/createNumberMask';

import {MaskType} from 'types/Mask';

export const regex = {
  ALPHA: /^[A-Za-z]+$/,
  ALPHA_AND_SPACES: /^[a-zA-Z ]*$/,
  SINGLE_ALPHA: /^[A-Za-z]/,
  ALPHA_NUMERIC: /^[a-zA-Z0-9]+$/,
  ALPHA_NUMERIC_WITH_MASK: /^[a-zA-Z0-9*]+$/,
  NON_NUMERIC: /[^0-9]/g,
  NUMERIC: /^[0-9]+$/,
  AMICA_ACCOUNT_NUMBER: /^[N|n][0-9]+$/,
  AMICA_POLICY_NUMBER: /[^A-Za-z0-9]/g,
  EMAIL:
    /^((?![*])[\w#$%&amp;'*+/=?`{}~-]+)(?:\.[\w#$%&amp;'*+/=?`{}~-]+)*@(?:[A-Za-z0-9](?:[A-Za-z0-9-]*[A-Za-z0-9])?\.)+(?:[A-Za-z0-9](?:[A-Za-z0-9-]*[A-Za-z0-9])?$)/,
  STREET: /^[a-zA-Z0-9.,#/&(){}[\]'-]+[a-zA-Z0-9 .,#/&(){}[\]'-]*$/,
  PO_BOX: /\b(?:p\.?\s*o\.?|post\s+office)(\s+)?(?:box|[0-9]*)?\b/,
  CITY: /^[a-zA-Z.-]+[a-zA-Z .-]*$/,
  ZIP_CODE: /^\d{5}$/,
  ZIP_PLUS4: /^\d{9}$/,
  ZIP_VALIDATION: /^\d{5}(?:[-]\d{4})?$/,
  DOMESTIC_PHONE: /^\d{3}[-\u2011]\d{3}[-\u2011]\d{4}$/,
  GMT_OFFSET: /(GMT\W*\d{4})/,
  /* RegExp to identify a `full-date` formatted string. From rfc3339, full-date (which specifies RAML's date-only format): */
  RAML_FULL_DATE: /(\d{4})-(\d{2})-(\d{2})/,
  /* RegExp to test if a date-only-formatted string is formatted correctly. */
  DATE_ONLY: /^(\d{4})-(\d{2})-(\d{2})$/i,
  DATE_ONLY_mmddYYYY_SLASH: /^(\d{1,2})\/(\d{1,2})\/(\d{4})$/,
  DATE_ONLY_mmddYY_SLASH: /^(\d{1,2})\/(\d{1,2})\/(\d{2})$/,
  YEAR: /^\d{4}$/,
  BANK_ACCOUNT_NAME_YUP: /^[A-Za-z .,\-']+$/,
  VIN: /^[A-HJ-NPR-Za-hj-npr-z0-9]{17}$/,
  CUSTOMER_NAME: /^[a-zA-Z][a-zA-Z '.-]*$/,
  SSN: /^(?!000|666)[0-8][0-9]{2} (?!00)[0-9]{2} (?!0000)[0-9]{4}$/,
  SSN_WITH_MASK: /^[*]{3} [*]{2} (?!0000)[0-9]{4}$/,
  SSN4: /^\d{4}$/,
  MUTUAL_POLICY_NUMBER: /^[A-Z0-9]{6}-[A-Z0-9]{4}$/,
  VEHICLE_TITLE_CASE_DELIMITER: /([()/\-+_=[\]'":;,.<>!@#$%^&* ])/,
  CLAIM_FILE_NUMBER: [/\d/, /\d/, /\d/, /\d/, /\d/, /\d/, /\d/, /\d/, /\d/, /\d/, /\d/],
  LIFE_POLICY_NUMBER: /^[A-Z0-9]{1}-[A-Z0-9]{9}$/,
  POLICY_NUMBER_LIFE: [/\w/, '-', /\w/, /\w/, /\w/, /\w/, /\w/, /\w/, /\w/, /\w/, /\w/],
  POLICY_NUMBER_MUTUAL: [/\w/, /\w/, /\w/, /\w/, /\w/, /\w/, '-', /\w/, /\w/, /\w/, /\w/],
  QR_CODE_CONSTRAINTS: {
    NUMERIC: /[^0-9]+/g,
    ALPHA: /[^a-zA-Z]+/g,
    NAME: /[^a-zA-Z '.-]+/g
  },
  SINGLE_OR_REPEATED_DIGITS: /^(\d)\1*$/
};

export const isRegex = (expression: RegExp | string): expression is RegExp =>
  (expression as RegExp).flags !== undefined;

const zipCodeMask = (zipcode: string) => {
  const digits = zipcode.replace(/[^0-9]+/g, '');
  return [/\d/, /\d/, /\d/, /\d/, /\d/, digits.length > 5 ? '-' : /\d/, /\d/, /\d/, /\d/, /\d/];
};

const nameMask = (name: string) =>
  new Array(name.length).fill(0).map((_, index) => (index === 0 ? /[a-zA-Z]/ : /[a-zA-Z '.-]/));

const addressMask = (address: string) => new Array(address.length).fill(0).map(() => /[a-zA-Z0-9 /#&,.'-]/);

const cityMask = (city: string) => new Array(city.length).fill(0).map(() => /[a-zA-Z0-9 .'-]/);

const companyNameMask = (companyName: string) =>
  new Array(companyName.length).fill(0).map((_, index) => (index === 0 ? /[a-zA-Z0-9]/ : /[a-zA-Z0-9 \-&'.,/()]/));

const lettersNumbersDashesMask = (stringIn: string) =>
  new Array(stringIn.length).fill(0).map((_, index) => (index === 0 ? /[a-zA-Z0-9]/ : /[a-zA-Z0-9 -]/));

const buildCurrencyMask =
  (
    maxLimit: number,
    excludeDecimals?: boolean,
    excludeDollarSign?: boolean,
    excludeAutomaticCents?: boolean
  ): MaskType =>
  (rawValue: string) => {
    const numberMask = createNumberMask({
      prefix: excludeDollarSign ? '' : '$',
      includeThousandsSeparator: true,
      allowDecimal: !excludeDecimals,
      requireDecimal: !excludeAutomaticCents,
      allowLeadingZeroes: false,
      integerLimit: maxLimit
    });
    const mask = numberMask(rawValue);

    const REGEX_DECIMALS = /\.([0-9]{1,2})/;
    const result = REGEX_DECIMALS.exec(rawValue);
    if (!excludeDecimals) {
      // In case there is only one decimal
      if (result && result[1].length < 2 && !excludeAutomaticCents) {
        mask.push('0');
      } else if (!result && !excludeAutomaticCents) {
        mask.push('0');
        mask.push('0');
      } else if (/\.(000)/.exec(rawValue)) {
        //user typed a leading zero after a decimal
        mask[mask.length - 3] = '0';
        mask[mask.length - 2] = '[]';
      }
    }
    return mask;
  };

const licenseNumberMask = (licenseNumber: string) =>
  new Array(licenseNumber.length).fill(0).map(() => /^[a-zA-Z0-9]+$/);

const piiLicenseNumberMask = (licenseNumber: string) =>
  new Array(licenseNumber.length).fill(0).map(() => /^[a-zA-Z0-9*]+$/);

const vinMask = (vin: string) => new Array(vin.length).fill(0).map(() => /^[A-HJ-NPR-Za-hj-npr-z0-9]$/);

const noSpacesMask = (text: string) => new Array(text.length).fill(0).map(() => /[^ ]/);

export const Mask = {
  PHONE: [/[1-9]/, /\d/, /\d/, '-', /\d/, /\d/, /\d/, '-', /\d/, /\d/, /\d/, /\d/],
  PHONE_EXTENSION: [/\d/, /\d/, /\d/, /\d/, /\d/],
  INTL_PHONE: new Array(15).fill(0).map(() => new RegExp('\\d')),
  ZIPCODE: zipCodeMask,
  WHOLE_DOLLAR_CURRENCY: buildCurrencyMask(10, true),
  CURRENCY: buildCurrencyMask(10),
  CURRENCY_NO_DOLLAR_SIGN: buildCurrencyMask(10, false, true),
  CURRENCY_NO_AUTOMATIC_CENTS: buildCurrencyMask(10, false, false, true),
  CURRENCY_NO_AUTOMATIC_CENTS_NO_DOLLAR_SIGN: buildCurrencyMask(10, false, true, true),
  CURRENCY_WHOLE_DOLLAR_NO_CENTS_NO_DOLLAR_SIGN: buildCurrencyMask(10, true, true, true),
  CURRENCY_WHOLE_DOLLAR_NO_CENTS_NO_OUTSIDE_DOLLAR_SIGN: buildCurrencyMask(10, true, false, true),
  TOW_AMOUNT_CURRENCY: buildCurrencyMask(5),
  TOW_AMOUNT_CURRENCY_NO_OUTSIDE_DOLLAR_SIGN: buildCurrencyMask(5),
  DATE: [/\d/, /\d/, '/', /\d/, /\d/, '/', /\d/, /\d/, /\d/, /\d/],
  TIME: [/\d/, /\d/, ':', /\d/, /\d/, ' ', /[ap]/i, /m/i],
  THOUSANDS: createNumberMask({prefix: ''}),
  MONTH: [/\d/, /\d/],
  YEAR: [/\d/, /\d/, /\d/, /\d/],
  HOUR: [/\d/, /\d/],
  MINUTES: [/\d/, /\d/],
  YEARS: [/\d/, /\d/],
  MONTHS: [/\d/, /\d/],
  UNITS: [/\d/, /\d/],
  NUMERIC: createNumberMask({prefix: ''}),
  SSN: [/\d*/, /\d*/, /\d*/, ' ', /\d*/, /\d*/, ' ', /\d/, /\d/, /\d/, /\d/],
  SSN4: [/\d/, /\d/, /\d/, /\d/],
  LICENSE_NUMBER: licenseNumberMask,
  PII_LICENSE_NUMBER: piiLicenseNumberMask,
  SMS_CODE: [/\d/, /\d/, /\d/, /\d/, /\d/, /\d/],
  SINGLE_ALPHA: [/[A-Za-z]/],
  PERSONAL_ACCESS_CODE: [/\d/, /\d/, /\d/, /\d/, /\d/, /\d/, /\d/, /\d/, /\d/, /\d/],
  NAME: nameMask,
  ADDRESS: addressMask,
  CITY: cityMask,
  VIN: vinMask,
  COMPANY_NAME_MASK: companyNameMask,
  VEHICLE_VESSEL_MASK: lettersNumbersDashesMask,
  LOAN_NUMBER_MASK: lettersNumbersDashesMask,
  NO_SPACES: noSpacesMask
};

/**
 * @param message - message to inspect
 * @returns True if there are 9, 14, 15 or 16 consecutive digits in the message
 *  after removing dashes and spaces, false otherwise.
 */
export const messageHasSensitiveData = (message: string) =>
  new RegExp('(^|[^\\d]|\\n)(\\d{9}|\\d{14,16})([^\\d]|\\n|$)').test(message.replace(/-|\s/g, ''));

/* https://developer.mozilla.org/en-US/docs/Web/JavaScript/Guide/Regular_Expressions#Escaping */
export const escapeRegExp = (str: string) => str.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');

/**
 * Validates whether or not a date string is in MM/dd/yyyy or M/d/yyyy format.
 * This function will only be invoked when validating raw input (not from a picker).
 * @param date - Date string
 * @returns True if the time string format is valid, false otherwise.
 */
export const isValidDateFormat = (date: string): boolean =>
  new RegExp('^(0?[1-9]|1[012])\\/(0?[1-9]|[12][0-9]|3[01])\\/\\d{4}$').test(date);

/**
 * Validates whether or not a time string is in HH:MM AM/PM format.
 * This function will only be invoked when validating raw input (not from a picker).
 * @param time - Time string
 * @returns True if the time string format is valid, false otherwise.
 */
export const isValidTimeFormat = (time: string): boolean =>
  new RegExp('^((1[0-2]|0?[1-9]):([0-5][0-9]) ([AaPp][Mm]))$').test(time);

/**
 * @param accountOrPolicyNumber - string to test for account or policy number
 * @returns true if an alpaha numeric string of length 10
 *  after removing dashes and spaces, false otherwise.
 */
export const isPolicyOrAccountNumber = (accountOrPolicyNumber: string): boolean =>
  accountOrPolicyNumber ? new RegExp('^[0-9a-zA-Z]{2,10}$').test(accountOrPolicyNumber.replace(/-|\s/g, '')) : false;

/**
 * @param policyNumber - string to test for policy number - life
 * @returns true if a numeric string of length 10
 *  after removing dashes and spaces, false otherwise.
 */
export const isLifePolicyNumber = (policyNumber: string): boolean =>
  policyNumber ? new RegExp('^[0-9]{2,10}$').test(policyNumber.replace(/-|\s/g, '')) : false;
